import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import type { Asset } from 'types/asset.types';
import type { Device } from 'types/device.types';

export type QuickRangeType =
  | 'Today'
  | '1D'
  | '7D'
  | '14D'
  | '1Y'
  | 'All'
  | 'Custom';
export type AggregationType = 'avg_val' | 'max_val' | 'min_val';
export type ChartType = 'Individual' | 'Grouped';

export interface HighGrainFilters {
  startDateTime: number;
  endDateTime: number;
  execute: boolean;
}

export interface RegularFilters {
  startDate: number;
  endDate: number;
  activeButton: QuickRangeType;
}

export interface SensorAnalysisState {
  dataType: 'regular' | 'highGrain';
  aggregation: AggregationType;
  highGrainFilters: HighGrainFilters;
  regularFilters: RegularFilters;
  selectedAsset: Asset;
  selectedMakeModel: string;
  selectedTags: string[];
  chartType: 'Individual' | 'Grouped';
}

export const initialAssetState: Asset = {
  assetId: '',
  assetVin: '',
  assetType: 'haul_truck',
  make: '',
  bumperNumber: '',
  engineHoursOffset: -1,
  device: {} as const as Device,
  isActive: false,
  model: '',
  odometerOffset: -1,
  fuelSource: '',
  operators: [],
  externalId: '',
  assetNotes: [],
};

const initialState: SensorAnalysisState = {
  dataType: 'regular',
  aggregation: 'avg_val',
  highGrainFilters: {
    startDateTime: dayjs().subtract(4, 'hour').valueOf(), // 4 hours ago
    endDateTime: dayjs().valueOf(),
    execute: false,
  },
  regularFilters: {
    activeButton: '7D',
    startDate: dayjs().startOf('day').valueOf(), // today
    endDate: dayjs().endOf('day').valueOf(), // today
  },
  selectedAsset: initialAssetState,
  selectedMakeModel: '',
  selectedTags: [],
  chartType: 'Individual',
};

export const sensorAnalysisSlice = createSlice({
  name: 'sensorAnalysis',
  initialState,
  reducers: {
    setDataType: (state, action: PayloadAction<'regular' | 'highGrain'>) => {
      state.dataType = action.payload;
    },
    setActiveButton: (state, action: PayloadAction<QuickRangeType>) => {
      state.regularFilters.activeButton = action.payload;
    },
    setAggregation: (state, action: PayloadAction<AggregationType>) => {
      state.aggregation = action.payload;
    },
    setHighGrainStartDateTime: (state, action: PayloadAction<number>) => {
      state.highGrainFilters.startDateTime = action.payload;
    },
    setHighGrainEndDateTime: (state, action: PayloadAction<number>) => {
      state.highGrainFilters.endDateTime = action.payload;
    },
    setHighGrainExecute: (state, action: PayloadAction<boolean>) => {
      state.highGrainFilters.execute = action.payload;
    },
    setSelectedAsset: (state, action: PayloadAction<Asset>) => {
      state.selectedAsset = action.payload;
    },
    resetSelectedAsset: (state) => {
      state.selectedAsset = initialAssetState;
    },
    setSelectedMakeModel: (state, action: PayloadAction<string>) => {
      state.selectedMakeModel = action.payload;
      state.selectedAsset = initialAssetState;
    },
    setRegularStartEndDate(
      state,
      action: PayloadAction<{ startDate: number; endDate: number }>
    ) {
      state.regularFilters.startDate = action.payload.startDate;
      state.regularFilters.endDate = action.payload.endDate;
    },
    setSelectedTags(state, action: PayloadAction<string[]>) {
      state.selectedTags = action.payload;
    },
    setChartType(state, action: PayloadAction<ChartType>) {
      state.chartType = action.payload;
    },
  },
});

export const {
  setDataType,
  setActiveButton,
  setAggregation,
  setHighGrainStartDateTime,
  setHighGrainEndDateTime,
  setHighGrainExecute,
  setSelectedAsset,
  resetSelectedAsset,
  setSelectedMakeModel,
  setRegularStartEndDate,
  setSelectedTags,
  setChartType,
} = sensorAnalysisSlice.actions;
export default sensorAnalysisSlice.reducer;
